<template lang="">
<!--~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Start Server Requirements Page
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~-->
<div class="ios-section doc-wrapper doc-inner">
    <div class="row">
        <div class="col-xl-12">
            <div class="ios-wrapper">
                <h2 class="inner-title">Installing and Releasing iOS App on App Store: Step-by-Step Guide</h2>
                <small >Before building and releasing your app on the App Store, you need to set up a place for it using App Store Connect and register a unique bundle ID for your app. Here’s how you can accomplish this:</small>
                <ul class="configure-list">
                    <li class="mb-10"><b>Step 1:</b> Set Up Bundle ID on Apple Developer Account
                        <ol>
                            <li>Log in to your <a href="https://appstoreconnect.apple.com/" class="redirect-link">[App Store Connect account]</a></li>
                            <li>Open the “App IDs” page.</li>
                            <li>Click on the “+” button to create a new Bundle ID.</li>
                            <li>Fill in the necessary information, including the App Name and Explicit App ID.</li>
                            <li>If your app requires specific services, select them and click Continue.</li>
                            <li>Review the details and click Register to finish.</li>
                            <li>In the account dashboard, select “My Apps”.</li>
                            <li>Click on “+” and select “New App”.</li>
                            <li>Fill in your app details, ensuring iOS is selected, then click Create.</li>
                            <li>From the sidebar, select “App Information”.</li>
                            <li>In the General Information section, select the Bundle ID that you registered above.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 2:</b> Adjust Xcode Project Settings
                        <ol>
                            <li>Open <code>Runner.xcworkspace</code> located inside your app’s iOS folder using Xcode.</li>
                            <li>In the Xcode project navigator, select the Runner project.</li>
                            <li>Go to the General tab.</li>
                            <li>Fill out the information in the Identity section and ensure the Bundle Identifier matches the one registered on App Store Connect.</li>
                            <li>In the Signing section, ensure “Automatically manage signing” is checked and select your team.</li>
                            <li>Fill out the rest of the information as needed.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 3:</b> Update App’s Icon
                        <ol>
                            <li>Select <code>Assets.xcassets</code> in the Runner folder from Xcode’s project navigator.</li>
                            <li>Update your app’s icon as required.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 4:</b> Build and Release
                        <ol>
                            <li>From the command line, run:</li>
                            <pre class="prettyprint mb-0">flutter build ios</pre>
                            <li>Reopen <code>Runner.xcworkspace</code> in Xcode.</li>
                            <li>Select Product -> Scheme -> Runner.</li>
                            <li>Select Product -> Destination -> Generic iOS Device.</li>
                            <li>Select Product -> Archive to produce a build archive.</li>
                            <li>From the Xcode Organizer window, select your iOS app from the sidebar, then select the build archive you just produced.</li>
                            <li>Click the Validate… button to build.</li>
                            <li>Once the archive is successfully validated, click Upload to App Store.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 5:</b> Fill in App Details
                        <ol>
                            <li>From the command line, run:</li>
                            <li>Select your newly created app from the list.</li>
                            <li>Go to the “App Information” section.</li>
                            <li>Fill in all required details such as the app name, subtitle, privacy policy URL, and app icon.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 6:</b> Upload App Screenshots
                        <ol>
                            <li>Go to the “App Store” section.</li>
                            <li>Upload screenshots of your app for different device sizes.</li>
                            <li>Ensure that the screenshots comply with Apple’s guidelines regarding content and dimensions.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 7:</b> Configure App Metadata
                        <ol>
                            <li>Go to the “App Store” section.</li>
                            <li>Fill in the app’s metadata, including the description, keywords, categories, and ratings.</li>
                            <li>Provide any additional information required by Apple, such as contact information and support URL.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 8:</b> Set Pricing and Availability
                        <ol>
                            <li>Go to the “Pricing and Availability” section.</li>
                            <li>Set the pricing tier for your app.</li>
                            <li>Choose the territories where you want your app to be available.</li>
                            <li>Set the release date and availability options for your app.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 9:</b> Submit App for Review
                        <ol>
                            <li>Go to the “App Store” section.</li>
                            <li>Review all the details of your app to ensure accuracy.</li>
                            <li>Click on “Submit for Review” to submit your app for review by Apple’s App Review team.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 10:</b> Await Review
                        <ol>
                            <li>Once you’ve submitted your app for review, it will undergo a review process by Apple’s App Review team. This process may take several days.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 11:</b> Review and Respond to Feedback
                        <ol>
                            <li>During the review process, Apple may provide feedback or request additional information about your app. Make sure to respond promptly and address any issues raised by the review team.</li>
                        </ol>
                    </li>
                    <li class="mb-10"><b>Step 12:</b> Release Your App
                        <ol>
                            <li>Once your app has been approved, you will receive a notification from Apple. You can then proceed to release your app on the App Store by clicking on the “Release” button in the App Store section of your app’s dashboard.</li>
                        </ol>
                    </li>
                    <p><b class="text--danger">Conclusion:</b> By following these steps, you can successfully submit your app for review and release it on the App Store Console. Make sure to adhere to all App Store guidelines and requirements to ensure a smooth review process. Good luck with your app submission!</p>
                </ul>
            </div>
        </div>
    </div>
</div>
<div class="page-change-area">
    <div class="navigation-wrapper">
        <router-link :to="{ name: 'AndroidAppDeployment'}" class="left"><i class="las la-angle-left me-1"></i> Android App Deployment</router-link>
        <router-link :to="{ name: 'WebsiteVersionUpdate'}" class="right">Website Version Update <i class="las la-angle-right ms-1"></i></router-link>
    </div>
</div>
<!--~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    End Server Requirements Page
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~-->
</template>

<script>
export default {
    name: 'iOSAppDeploymentPage',
}
</script>